import React, { useMemo, useState } from "react";
import { ChevronDownIcon, XCircleFillIcon } from '@primer/octicons-react';
import { Box, Button, SelectPanel } from "@primer/react";
import { VariantType } from '@primer/react/lib/Button/types';
import { useCommonContext } from "../../contexts/common";
import { sf_hinhthucmiengiam } from "../../models/response/category/sf_hinhthucmiengiam";

type ITagBoxHinhThucMienGiamProps = {
    nhom_mien_giam: number | number[],
    isReadonly?: boolean,
    value?: number[],
    width?: number | string,
    onValueChanged: (id: number[], data?: any[]) => void,
    className?: string,
    isShowClearButton?: boolean,
    preText?: string,
    stylingMode?: "outlined" | "filled" | "underlined",
    variant?: VariantType,
    maxWidth?: any,
    maxItemsDisplayText?: number,
    dataSource?: sf_hinhthucmiengiam[]
}

const TagBoxHinhThucMienGiam = (props: ITagBoxHinhThucMienGiamProps) => {
    const { translate } = useCommonContext();
    const [open, setOpen] = useState(false);
    const [filter, setFilter] = useState("");

    const dataSource = useMemo(() => {
        const nhomMienGiamIds = Array.isArray(props.nhom_mien_giam) 
            ? props.nhom_mien_giam 
            : (props.nhom_mien_giam !== undefined && props.nhom_mien_giam !== null && props.nhom_mien_giam !== 0 
                ? [props.nhom_mien_giam] 
                : []);
        
        if (nhomMienGiamIds.length === 0) return [];
        return props.dataSource?.filter(x => nhomMienGiamIds.includes(x.nhom_mien_giam)) || [];
    }, [props.nhom_mien_giam, props.dataSource]);

    const items = useMemo(() => {
        return dataSource.map(x => ({
            id: x.id,
            text: x.hinh_thuc_mien_giam,
            trailingVisual: `Nhóm ${x.nhom_mien_giam}`,
            search: `${x.hinh_thuc_mien_giam} ${x.ma_hinh_thuc || ''} Nhóm ${x.nhom_mien_giam}`,
            data: x
        }));
    }, [dataSource]);

    const filteredData = useMemo(() => {
        return items.filter(item =>
            item.search.toLowerCase().includes(filter.toLowerCase())
        );
    }, [items, filter]);

    const selectedValues = useMemo(() => {
        return props.value || [];
    }, [props.value]);

    const selectedData = useMemo(() => {
        return items.filter(item => selectedValues.includes(item.id));
    }, [items, selectedValues]);

    const displayText = useMemo(() => {
        if (selectedData.length === 0) {
            return props.preText || translate("Chọn hình thức MG");
        }
        if (selectedData.length === 1) {
            return selectedData[0].text;
        }
        const maxItems = props.maxItemsDisplayText || 3;
        if (selectedData.length <= maxItems) {
            return selectedData.map(item => item.text).join(', ');
        }
        return `${selectedData.length} items selected`;
    }, [selectedData, props.preText, props.maxItemsDisplayText, translate]);

    const handleSelectedChange = (data: any) => {
        if (!props.isReadonly) {
            if (Array.isArray(data)) {
                const ids = data.map(item => item.id);
                const selectedItems = data.map(item => item.data);
                props.onValueChanged(ids, selectedItems);
            } else if (data) {
                props.onValueChanged([data.id], [data.data]);
            } else {
                props.onValueChanged([], []);
            }
        }
    };

    const handleClear = () => {
        if (props.isShowClearButton && !props.isReadonly) {
            props.onValueChanged([], []);
        }
    };

    return (
        <Box sx={{ width: props.width || '100%' }}>
            <SelectPanel
                renderAnchor={({ children, 'aria-labelledby': ariaLabelledBy, ...anchorProps }) => (
                    <Button
                        variant={props.variant}
                        disabled={props.isReadonly}
                        sx={{
                            width: '100%',
                            height: 33,
                        }}
                        trailingAction={ChevronDownIcon}
                        aria-labelledby={` ${ariaLabelledBy}`}
                        {...anchorProps}
                    >
                        <p style={{ 
                            maxWidth: props.maxWidth, 
                            overflow: "hidden", 
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            fontSize: '12px',
                        }}>
                            {displayText}
                        </p>
                    </Button>
                )}
                title={
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                        <Box sx={{ flex: 1 }}>
                            {translate("Chọn hình thức MG")}
                        </Box>
                        {props.isShowClearButton && selectedData.length > 0 && (
                            <Button
                                trailingVisual={XCircleFillIcon}
                                variant='invisible'
                                sx={{
                                    color: "danger.emphasis"
                                }}
                                onClick={handleClear}
                            >
                                Bỏ chọn
                            </Button>
                        )}
                    </Box>
                }
                placeholderText="Tìm kiếm..."
                open={open}
                onOpenChange={setOpen}
                items={filteredData}
                selected={selectedData}
                onSelectedChange={handleSelectedChange}
                onFilterChange={setFilter}
                showItemDividers={true}
                overlayProps={{ width: 'large', height: 'medium' }}
            />
        </Box>
    );
}

export default TagBoxHinhThucMienGiam;